/**
 * Recognize audience metrics — Combined Reach and Earned Media Value.
 * EMV = engagements × CPM × posting frequency (1: a shopper advocates once a year).
 */

export const RECOGNIZE_PLATFORMS = [
  "instagram",
  "tiktok",
  "youtube",
  "x",
] as const;

export type RecognizePlatform = (typeof RECOGNIZE_PLATFORMS)[number];

/** Display label for each platform, shared across every Recognize surface. */
export const PLATFORM_LABEL: Record<RecognizePlatform, string> = {
  instagram: "Instagram",
  tiktok: "TikTok",
  youtube: "YouTube",
  x: "X",
};

/** Reveal engine social keys (it still calls X "twitter") → the shared enum. */
export const REVEAL_SOCIAL_KEY_TO_PLATFORM: Record<string, RecognizePlatform> =
  {
    instagram: "instagram",
    tiktok: "tiktok",
    youtube: "youtube",
    twitter: "x",
  };

/** Audience size band. Boundaries round up: exactly 10,000 is micro, not nano. */
export type AudienceTier = "nano" | "micro" | "macro" | "mega";

/** The first 1,000 followers sit in a zero-rate band, so EMV is continuous here. */
export const UNPRICED_FOLLOWERS = 1000;

export const ANNUAL_POSTING_FREQUENCY = 1;

interface Band {
  tier: AudienceTier;
  /** Followers strictly above this count fall into the band. */
  from: number;
  /** ...up to and including this count. */
  to: number;
}

/**
 * Marginal bands, like tax brackets: a rate applies only to followers inside its
 * band, so growth never lowers EMV and each additional follower is worth less.
 */
const BANDS: readonly Band[] = [
  { tier: "nano", from: UNPRICED_FOLLOWERS, to: 10_000 },
  { tier: "micro", from: 10_000, to: 100_000 },
  { tier: "macro", from: 100_000, to: 1_000_000 },
  { tier: "mega", from: 1_000_000, to: Number.POSITIVE_INFINITY },
];

/**
 * x's bands are organic reach rates, not engagement rates like the others, and
 * its source stops at "100,000+ = 0.5–2%" — macro/mega split that range.
 */
const ENGAGEMENT_RATE: Record<
  RecognizePlatform,
  Record<AudienceTier, number>
> = {
  instagram: { nano: 0.06, micro: 0.035, macro: 0.024, mega: 0.014 },
  tiktok: { nano: 0.09, micro: 0.06, macro: 0.05, mega: 0.025 },
  youtube: { nano: 0.055, micro: 0.03, macro: 0.0225, mega: 0.015 },
  x: { nano: 0.075, micro: 0.035, macro: 0.015, mega: 0.0075 },
};

/** Applied undivided rather than per-thousand, per the Recognize EMV spec. */
const CPM_USD: Record<RecognizePlatform, number> = {
  instagram: 12.5,
  tiktok: 10.5,
  youtube: 25,
  x: 6,
};

export interface PlatformAudience {
  platform: RecognizePlatform;
  /** null = profile found but count not public. Never coerce to 0. */
  followers: number | null;
}

/** The band a total follower count lands in, or null when it earns nothing. */
export function audienceTier(followers: number): AudienceTier | null {
  if (!Number.isFinite(followers)) {
    return null;
  }
  for (let i = BANDS.length - 1; i >= 0; i--) {
    const band = BANDS[i]!;
    if (followers >= band.from) {
      return band.tier;
    }
  }
  return null;
}

/** Expected engagements per post, summed across the marginal bands. */
export function expectedEngagements(audience: PlatformAudience): number {
  const { platform, followers } = audience;
  if (followers == null || !Number.isFinite(followers)) {
    return 0;
  }
  const rates = ENGAGEMENT_RATE[platform];
  let engagements = 0;
  for (const band of BANDS) {
    if (followers <= band.from) {
      break;
    }
    engagements +=
      (Math.min(followers, band.to) - band.from) * rates[band.tier];
  }
  return engagements;
}

/** Earned media value in USD for one platform profile. */
export function earnedMediaValue(audience: PlatformAudience): number {
  const cpm = CPM_USD[audience.platform];
  return expectedEngagements(audience) * cpm * ANNUAL_POSTING_FREQUENCY;
}

/** Sum of followers across every platform, including those too small to earn EMV. */
export function combinedReach(audiences: readonly PlatformAudience[]): number {
  return audiences.reduce((sum, a) => sum + (a.followers ?? 0), 0);
}

export function totalEarnedMediaValue(
  audiences: readonly PlatformAudience[],
): number {
  return audiences.reduce((sum, a) => sum + earnedMediaValue(a), 0);
}
